import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Loader2, Search } from 'lucide-react';
import { fieldClass, primaryBtnClass } from '../styles';
import type { AiModelOption, GeneratorKey } from '../../aitest/types';
import { listAdminAiModels, patchAdminAiModel, refreshAiModels } from './api';

const GENERATORS: Array<{ key: GeneratorKey; label: string }> = [
  { key: 'source_of_truth', label: 'Source of Truth' },
  { key: 'jtbd', label: 'Jobs to be Done' },
  { key: 'tension', label: 'Tension' },
  { key: 'benefit', label: 'Benefit' },
  { key: 'recipe_attributes', label: 'Recipe Card Attributes' },
  { key: 'written_concept', label: 'Written Concept' },
  { key: 'visual_concept', label: 'Visual Concept' },
  { key: 'visual_concept_regenerate', label: 'Visual Concept Regenerate' },
  { key: 'visual_concept_edit', label: 'Visual Concept Edit' },
];

type CapabilityFilter = '' | 'reasoning' | 'web_search' | 'image';

const ModelsPanel: React.FC = () => {
  const [models, setModels] = useState<AiModelOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [provider, setProvider] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [capability, setCapability] = useState<CapabilityFilter>('');
  const [generator, setGenerator] = useState<GeneratorKey | ''>('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setModels(await listAdminAiModels());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load models');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return models.filter((model) => {
      if (
        needle &&
        !`${model.model_id} ${model.label}`.toLowerCase().includes(needle)
      ) return false;
      if (provider && model.provider !== provider) return false;
      if (activeOnly && !model.is_active) return false;
      if (generator && !model.allowed_generators.includes(generator)) return false;
      if (capability === 'reasoning' && !model.supports_reasoning_effort && !model.supports_thinking) return false;
      if (capability === 'web_search' && !model.supports_web_search) return false;
      if (capability === 'image' && !model.supports_image_generation) return false;
      return true;
    });
  }, [models, search, provider, activeOnly, capability, generator]);

  const update = async (id: number, payload: Partial<AiModelOption>) => {
    try {
      const updated = await patchAdminAiModel(id, payload);
      setModels((prev) => prev.map((model) => (model.id === id ? updated : model)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update model');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    setMessage(null);
    try {
      await refreshAiModels();
      await load();
      setMessage(
        'Catalog refreshed. Reasoning, web search, and image ticks were rewritten from the current catalog.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading models…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-[#626262]">
          Activate models, set optional USD cost per 1 million tokens, and choose which generators may use them.
          Capability ticks are inferred on every refresh and cannot be edited.
        </p>
        <button
          type="button"
          onClick={() => void handleRefresh()}
          disabled={refreshing}
          className={primaryBtnClass}
        >
          {refreshing ? 'Refreshing…' : 'Refresh catalog'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <label className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#8A8A8A]" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search model ID or display name"
            className={`${fieldClass} pl-9`}
          />
        </label>
        <select value={provider} onChange={(e) => setProvider(e.target.value)} className={fieldClass}>
          <option value="">All providers</option>
          <option value="openai">OpenAI</option>
          <option value="gemini">Gemini</option>
          <option value="anthropic">Anthropic</option>
        </select>
        <select
          value={capability}
          onChange={(e) => setCapability(e.target.value as CapabilityFilter)}
          className={fieldClass}
        >
          <option value="">All capabilities</option>
          <option value="reasoning">Reasoning</option>
          <option value="web_search">Web search</option>
          <option value="image">Image generation</option>
        </select>
        <select
          value={generator}
          onChange={(e) => setGenerator(e.target.value as GeneratorKey | '')}
          className={fieldClass}
        >
          <option value="">All generator tags</option>
          {GENERATORS.map((item) => (
            <option key={item.key} value={item.key}>{item.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 px-2 text-sm whitespace-nowrap">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(event) => setActiveOnly(event.target.checked)}
          />
          Active only
        </label>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {message && <p className="text-sm text-emerald-700">{message}</p>}
      <p className="text-[12px] text-[#8A8A8A]">
        Showing {filtered.length} of {models.length} models. 1M input/output cost is USD per 1 million tokens; leave blank to show tokens only in run history.
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-[12px] text-[#8A8A8A]">
              <th className="py-2 pr-3">Active</th>
              <th className="py-2 pr-3">Provider</th>
              <th className="py-2 pr-3">Model ID</th>
              <th className="py-2 pr-3">Display name</th>
              <th className="py-2 pr-3">1M input cost</th>
              <th className="py-2 pr-3">1M output cost</th>
              <th className="py-2 pr-3">Reasoning</th>
              <th className="py-2 pr-3">Web search</th>
              <th className="py-2 pr-3">Image</th>
              <th className="py-2 pr-3">Generators</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((model) => (
              <tr key={model.id} className="border-t border-[#F0F0F0] align-top">
                <td className="py-2 pr-3">
                  <input
                    type="checkbox"
                    checked={model.is_active}
                    onChange={(e) => void update(model.id, { is_active: e.target.checked })}
                  />
                </td>
                <td className="py-2 pr-3">{model.provider}</td>
                <td className="py-2 pr-3 min-w-[180px]">
                  <div className="font-mono text-[12px]">{model.model_id}</div>
                  <details className="mt-1">
                    <summary className="cursor-pointer text-[11px] text-[#3da3d4]">
                      Provider metadata
                    </summary>
                    <pre className="mt-1 max-w-sm whitespace-pre-wrap text-[10px] text-[#626262]">
                      {JSON.stringify(model.raw_capabilities, null, 2)}
                    </pre>
                  </details>
                </td>
                <td className="py-2 pr-3">
                  <input
                    value={model.label}
                    onChange={(e) =>
                      setModels((prev) => prev.map((row) => (
                        row.id === model.id ? { ...row, label: e.target.value } : row
                      )))
                    }
                    onBlur={(e) => void update(model.id, { label: e.target.value })}
                    className={`${fieldClass} min-w-[150px]`}
                  />
                </td>
                {([
                  ['cost_per_1m_input', model.cost_per_1m_input],
                  ['cost_per_1m_output', model.cost_per_1m_output],
                ] as const).map(([key, value]) => (
                  <td key={key} className="py-2 pr-3">
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      inputMode="decimal"
                      value={value ?? ''}
                      placeholder="—"
                      title="USD per 1 million tokens"
                      onChange={(e) =>
                        setModels((prev) => prev.map((row) => (
                          row.id === model.id ? { ...row, [key]: e.target.value || null } : row
                        )))
                      }
                      onBlur={(e) => {
                        const trimmed = e.target.value.trim();
                        const next = trimmed === '' ? null : trimmed;
                        void update(model.id, { [key]: next });
                      }}
                      className={`${fieldClass} min-w-[110px]`}
                    />
                  </td>
                ))}
                {([
                  model.supports_reasoning_effort || model.supports_thinking,
                  model.supports_web_search,
                  Boolean(model.supports_image_generation),
                ] as const).map((checked, index) => (
                  <td key={index} className="py-2 pr-3">
                    <input type="checkbox" checked={checked} disabled readOnly />
                  </td>
                ))}
                <td className="py-2 pr-3 min-w-[190px]">
                  <details>
                    <summary className="flex cursor-pointer list-none items-center gap-1 text-[12px]">
                      <ChevronDown className="h-3 w-3" />
                      {model.allowed_generators.length
                        ? `${model.allowed_generators.length} selected`
                        : 'None — not selectable'}
                    </summary>
                    <div className="mt-2 space-y-1 rounded border border-[#E8E8E8] bg-white p-2">
                      {GENERATORS.map((item) => (
                        <label key={item.key} className="flex items-center gap-2 text-[12px]">
                          <input
                            type="checkbox"
                            checked={model.allowed_generators.includes(item.key)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...model.allowed_generators, item.key]
                                : model.allowed_generators.filter((key) => key !== item.key);
                              void update(model.id, { allowed_generators: next });
                            }}
                          />
                          {item.label}
                        </label>
                      ))}
                    </div>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {models.length === 0 && (
        <p className="text-sm text-[#8A8A8A]">
          No models yet. Refresh the catalog to discover OpenAI and Gemini models.
        </p>
      )}
    </div>
  );
};

export default ModelsPanel;
